import React, { useContext } from "react";
import { withRouter, RouteComponentProps } from "react-router";
import { Link } from "react-router-dom";
import { useFlags } from "launchdarkly-react-client-sdk";
import {
  useTranslate,
  useQueryParams,
  hasGlobalSession,
  getLoginUrl,
} from "../../helpers";
import heroImage from "../../assets/new_landing_page_hero@2x.jpg";
import {
  BadgeWithIcon,
  CreateAccountIcon,
  ContinueWithoutLoggingInIcon,
} from "../../components";
import { AppStore } from "../../app/store";
import { Routes } from "../../app";
import { initiateLogin } from "../../helpers";
import { LoginForm } from "./LoginForm";
import { planTypeGa4EventMap } from "../../components/CoverageSelector/constants";
import { CoverageSelectorSelectedValues } from "../../components/CoverageSelector/types";
import {
  CoverageSelector,
  CoverageSelectorV2,
} from "../../components/CoverageSelector";
import { GetStarted } from "./GetStarted";
import { HeaderGroup } from "./HeaderGroup";
import { destinationMap, updateStates } from "./routingHelpers";
import routes from "../../app/routes";
import { isFromMedigap } from "../../helpers/routeHelpers";
import classNames from "classnames";
import {
  AnalyticsActionType,
  AnalyticsButton,
  Ga4Event,
  Ga4EventType,
} from "../../app/contexts/Analytics/types";

const LandingPage: React.FunctionComponent<RouteComponentProps> = props => {
  const t = useTranslate();
  const { history, location, match } = props;
  const { state, dispatch } = useContext(AppStore.AppContext);
  useQueryParams({ history, location, match });
  const flags = useFlags();
  const { feEnableAuthentication, tmpFeEnableNewCoverageSelector } = flags;
  const url = window.location.href.split("/?")[0];
  const paceLinkUrl = `${url}${Routes.pace.landingPage}${location.search}`;
  const paceLinkText = t("ooe_landing.pace_plans");
  const isLoggedIn = hasGlobalSession(state);

  // @TODO - Is there any reason to try to render Summary without a beneficiary?
  // There is no check here for a bene, and by now we should have one, or if we
  // don't, should be logging out (since local session is now a meaningless proxy
  // for a previous authentication)
  // @see the `PrivateRoute` component, with test logic to actively logout if a user
  // has a `sid` cookie but no `beneficiary` in `state`
  if (isLoggedIn && !isFromMedigap(url)) {
    history.replace(routes.summary.landingPage);
  }

  const onStart = (
    selectedValues: CoverageSelectorSelectedValues,
    analyticsButton?: AnalyticsButton
  ) => {
    updateStates(selectedValues, dispatch);
    if (selectedValues.coverageType) {
      const destination = destinationMap[selectedValues.coverageType];
      const linkUrl = `${url}${destination}${location.search}`;

      if (analyticsButton) {
        dispatch({
          type: AnalyticsActionType.SEND_BUTTON_ENGAGEMENT_EVENT,
          settings: {
            button: analyticsButton,
            linkUrl,
          },
        });
        dispatch({
          type: AnalyticsActionType.SEND_GA4_EVENT,
          settings: {
            event_name: Ga4Event.PLAN_FINDER_STARTED,
            event_type: Ga4EventType.UI_INTERACTION,
            link_text: analyticsButton.text,
            link_url: linkUrl,
            mct_plan_type: planTypeGa4EventMap[selectedValues.coverageType],
            mct_plan_year: selectedValues.coverageYear,
          },
        });
        dispatch({
          type: AnalyticsActionType.SEND_TEALIUM_EVENT,
          settings: {
            event_action: "Find Plans Landing Page - Plan Search",
            event_label: planTypeGa4EventMap[selectedValues.coverageType],
          },
        });
      }
      history.push(destination);
    }
  };

  const onClickLogin = (analyticsButton: AnalyticsButton) => {
    dispatch({
      type: AnalyticsActionType.SEND_BUTTON_ENGAGEMENT_EVENT,
      settings: {
        button: analyticsButton,
        linkUrl: getLoginUrl(state.language),
      },
    });
    initiateLogin(state.language, "", undefined);
  };

  return (
    <div className="mct-p-landing-page e2e-anonymous">
      <div className="mct-c-landing-page-hero">
        <div className="mct-c-landing-page-hero--left-col">
          <div className="mct-c-landing-page-hero__header-group">
            <HeaderGroup />
          </div>
          <GetStarted id="get-started-hero" />
        </div>
        <div className="mct-c-landing-page-hero--right-col">
          <img
            className="mct-c-landing-page-hero__image"
            src={heroImage}
            alt=""
          />
        </div>
      </div>
      <div
        className={classNames("mct-c-landing-page-main", {
          "mct-c-landing-page-main--v2": tmpFeEnableNewCoverageSelector,
        })}
      >
        <p className="mct-c-landing-page-main__heading">
          {t("landing_page.main.find_plans_header")}
        </p>
        {feEnableAuthentication && (
          <div className="mct-c-landing-page-main__login">
            <BadgeWithIcon svgIcon={CreateAccountIcon} theme="white">
              {t("landing_page.badge.use_account")}
            </BadgeWithIcon>
            <div className="ds-u-margin-top--3">
              <LoginForm onClickLogin={onClickLogin} />
            </div>
          </div>
        )}
        <div
          className={classNames("mct-c-landing-page-main__coverage", {
            "mct-c-landing-page-main__coverage--no-login": !feEnableAuthentication,
            "mct-c-landing-page-main__coverage--v2": tmpFeEnableNewCoverageSelector,
          })}
        >
          {!tmpFeEnableNewCoverageSelector && (
            <BadgeWithIcon
              svgIcon={ContinueWithoutLoggingInIcon}
              theme="primary-alt-lightest"
            >
              {t("landing_page.badge.continue_without_logging_in")}
            </BadgeWithIcon>
          )}
          <div
            className={classNames({
              "ds-u-measure--base": !feEnableAuthentication,
              "ds-u-margin-top--2": !tmpFeEnableNewCoverageSelector,
            })}
          >
            {tmpFeEnableNewCoverageSelector ? (
              <CoverageSelectorV2 onApply={onStart} />
            ) : (
              <CoverageSelector
                onApply={onStart}
                autoscrollWhenMenuOpens={true}
              />
            )}
          </div>
        </div>
        <GetStarted id="get-started-main" />
      </div>
      <div className="mct-c-landing-page-footer">
        <p>
          <Link
            to={`${Routes.pace.landingPage}${location.search}`}
            onClick={() => {
              dispatch({
                type: AnalyticsActionType.SEND_INTERNAL_LINK_CLICKED_EVENT,
                settings: {
                  linkText: paceLinkText,
                  linkUrl: paceLinkUrl,
                },
              });
              dispatch({
                type: AnalyticsActionType.SEND_GA4_EVENT,
                settings: {
                  event_name: Ga4Event.PACE_PLANS_CTA_CLICKED,
                  event_type: Ga4EventType.UI_INTERACTION,
                  link_text: paceLinkText,
                  link_url: paceLinkUrl,
                },
              });
            }}
          >
            {paceLinkText}
          </Link>
        </p>
      </div>
    </div>
  );
};

export default withRouter(LandingPage);
